import React from 'react';
import {
  ExternalLink,
  Link,
  NavLink,
} from 'components';
import {
  routes,
  CONTACTS_VALUES,
  ClientLang,
  DefaultLang,
} from 'appConstants';
import {
  _t,
  sendMetrics,
  phoneRefactor,
  openApplicationForm,
} from 'utils';

import iconPin from 'assets/img/footer-v4/icon-pin.svg';
import iconMail from 'assets/img/footer-v4/icon-mail.svg';
import iconPhone from 'assets/img/footer-v4/icon-phone.svg';
import iconSend from 'assets/img/footer-v4/icon-send.svg';
import iconUp from 'assets/img/footer-v4/icon-up.svg';
import logoWordmark from 'assets/img/footer-v4/logo-wordmark.svg';
import logoDippMark from 'assets/img/footer-v4/logo-dipp-mark.svg';
import logoDippText from 'assets/img/footer-v4/logo-dipp-text.svg';
import logoInvest from 'assets/img/footer-v4/logo-invest.svg';
import logoNprMark from 'assets/img/footer-v4/logo-npr-mark.svg';
import logoNprText from 'assets/img/footer-v4/logo-npr-text.svg';

import BannikonLogo from './BannikonLogo';
import styles from './styles.module.scss';

const DOCS_BASE = `${process.env.PUBLIC_URL || ''}/docs`;
const PRIVACY_POLICY_PDF = `${DOCS_BASE}/privacy-policy.pdf`;
const USER_AGREEMENT_PDF = `${DOCS_BASE}/user-agreement.pdf`;

const NAV_LINKS = [
  { to: routes.about.root, title: 'О нас' },
  { to: routes.resolutions.root, title: 'Услуги' },
  { to: routes.events.root, title: 'Мероприятия' },
  { to: `${routes.resolutions.service.root}/analytics`, title: 'Аналитика' },
  { to: routes.resolutions.root, title: 'Экспортерам' },
  { to: routes.media.root, title: 'Медиа' },
  { to: routes.vacancies.root, title: 'Вакансии' },
  { to: routes.contacts.root, title: 'Контакты' },
];

const TOOL_LINKS: Array<{
  title: string,
  to?: string,
  href?: string,
}> = [
  {
    href: 'https://t.me/main_mosprompt_bot',
    title: 'Чат-бот по мерам поддержки для экспортеров',
  },
  { to: routes.auth.exportTest.root, title: 'тест ADEPT 7' },
];

const SOCIAL = [
  { href: 'https://t.me/investmoscowru', title: 'Telegram' },
  { href: 'https://vk.com/mosprom', title: 'VK' },
];

const LEGAL = [
  { href: PRIVACY_POLICY_PDF, title: 'Политика конфиденциальности' },
  { href: USER_AGREEMENT_PDF, title: 'Пользовательское соглашение' },
];

const Footer = () => {
  const investHref = ClientLang === DefaultLang
    ? 'https://investmoscow.ru/'
    : 'https://en.investmoscow.ru/';

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.wrap}>
        <div className={styles.cards}>
          <div className={styles.cardMain}>
            <h2 className={styles.orgTitle}>
              {_t('АНО "Центр поддержки экспорта, промышленности и инвестиционной деятельности "МОСПРОМ"')}
            </h2>

            <div className={styles.cardMainBottom}>
              <div className={styles.block}>
                <h3 className={styles.blockTitle}>{_t('Цифровые аналитические инструменты')}</h3>
                <ul className={styles.list}>
                  {TOOL_LINKS.map((item) => (
                    <li key={item.title}>
                      {item.href ? (
                        <a
                          href={item.href}
                          className={styles.listLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`- ${_t(item.title)}`}
                        </a>
                      ) : (
                        <NavLink to={item.to || '/'} className={styles.listLink}>
                          {`- ${_t(item.title)}`}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div
                className={styles.block}
                itemScope
                itemType="http://schema.org/Organization"
              >
                <span className={styles.hidden} itemProp="name">Моспром</span>
                <h3 className={styles.blockTitle}>{_t('Контакты')}</h3>
                <div className={styles.contactsGroup}>
                  <ul className={styles.contacts}>
                    <li className={styles.contactRow}>
                      <img src={iconPin} alt="" className={styles.contactIconPin} />
                      <span itemProp="address" itemScope itemType="http://schema.org/PostalAddress">
                        <span itemProp="postalCode">{CONTACTS_VALUES.addrs.main.postal}</span>
                        {`, ${_t('город Москва')}, `}
                        <span itemProp="streetAddress">{_t(CONTACTS_VALUES.addrs.main.street)}</span>
                      </span>
                    </li>
                    <li className={styles.contactRow}>
                      <img src={iconMail} alt="" className={styles.contactIcon} />
                      <a
                        href={`mailto:${CONTACTS_VALUES.emails.main.value}`}
                        itemProp="email"
                        onClick={() => sendMetrics(null, ['email', 'click'])}
                      >
                        {CONTACTS_VALUES.emails.main.value}
                      </a>
                    </li>
                    <li className={styles.contactRow}>
                      <img src={iconPhone} alt="" className={styles.contactIcon} />
                      <div className={styles.phoneBlock}>
                        <a
                          href={`tel:${CONTACTS_VALUES.phones.main.value}`}
                          itemProp="telephone"
                          onClick={() => sendMetrics(null, ['telefon', 'tel'])}
                        >
                          {phoneRefactor(CONTACTS_VALUES.phones.main.value)}
                        </a>
                        {CONTACTS_VALUES.phones.main.extensions.map(({ code, label }) => (
                          <span key={code} className={styles.phoneExt}>
                            {`${_t('доб')}. (${code}) - ${_t(label)}`}
                          </span>
                        ))}
                      </div>
                    </li>
                  </ul>

                  <div className={styles.contactsBottom}>
                    <div className={styles.socialText}>
                      {SOCIAL.map(({ href, title }) => (
                        <a
                          key={title}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => sendMetrics(null, ['social_network', 'click'])}
                        >
                          {title}
                        </a>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.writeUs}
                      onClick={() => openApplicationForm({ type: 'footer' })}
                    >
                      <span>{_t('Написать нам')}</span>
                      <span className={styles.writeUsIcon}>
                        <img src={iconSend} alt="" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.cardNav}>
            <div className={styles.navBody}>
              <div className={styles.navTop}>
                <div className={styles.navBlock}>
                  <h3 className={styles.navTitle}>{_t('Навигация')}</h3>
                  <ul className={styles.list}>
                    {NAV_LINKS.map(({ to, title }) => (
                      <li key={title}>
                        <NavLink to={to} className={styles.listLink}>
                          {`- ${_t(title)}`}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  className={styles.upBtn}
                  aria-label={_t('Наверх')}
                  onClick={scrollTop}
                >
                  <img src={iconUp} alt="" />
                </button>
              </div>

              <div className={styles.legalBlock}>
                <ul className={styles.legal}>
                  {LEGAL.map(({ href, title }) => (
                    <li key={title}>
                      <a
                        href={href}
                        className={styles.legalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {_t(title)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className={styles.copyRow}>
              <span className={styles.copy}>
                {`© ${_t('МОСПРОМ')} 2021 - ${new Date().getFullYear()}`}
              </span>
              <ExternalLink href="https://bannikon.ru/" newTab className={styles.bannikon}>
                <BannikonLogo />
              </ExternalLink>
            </div>
          </div>
        </div>

        <div className={styles.brandRow}>
          <Link to={routes.main.root} className={styles.wordmark}>
            <img src={logoWordmark} alt="МОСПРОМ" />
          </Link>
          <div className={styles.partners}>
            <div className={styles.partnersTop}>
              <ExternalLink href="https://www.mos.ru/dipp/" newTab className={styles.dipp}>
                <img src={logoDippMark} alt="" className={styles.dippMark} />
                <img src={logoDippText} alt="ДИПП" className={styles.dippText} />
              </ExternalLink>
              <ExternalLink href={investHref} newTab className={styles.invest}>
                <img src={logoInvest} alt="investmoscow.ru" />
              </ExternalLink>
            </div>
            <ExternalLink
              href="https://национальныепроекты.рф/"
              newTab
              className={styles.npr}
            >
              <img src={logoNprMark} alt="" className={styles.nprMark} />
              <img src={logoNprText} alt="Национальные проекты" className={styles.nprText} />
            </ExternalLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
